import { TuiRoot, TuiLoader } from '@taiga-ui/core';
import { Component, inject, LOCALE_ID, OnInit, PLATFORM_ID } from '@angular/core';
import { ActivatedRoute, NavigationEnd, Router, RouterOutlet } from '@angular/router';
import { isPlatformBrowser, registerLocaleData } from '@angular/common';
import localeRu from '@angular/common/locales/ru';
import { catchError, concatMap, filter, forkJoin, of, take, tap } from 'rxjs';
import { OidcLoginService } from './core/services/oidc.login.service';
import { PromoCodeStoreService } from './store/promo-code.store';
// import { CrmWidgetListenerService } from './core/services/crm-widget-listener.service';
import { environment } from '../environments/environment';
import { ApiProductsService } from './shared/services/api/api-products.service';
import { ApiObjectsService } from './shared/services/api/api.objects.service';
import { ObjectTypesStore } from './store/object-types.store';
import { AreasStore } from './store/areas.store';
import { CategoriesStore } from './store/categories.store';
import { UserIdentityService } from './shared/services/api/user-identity.service';
import { ApiWebsocetService } from './shared/services/api/api-websocet.service';
import { CentrifugeService } from './core/services/centrifuge.service';
import { Crm2ChatWidgetService } from './core/services/crm2-chat-widget.service';
import { SeoService } from './core/services/seo.service';
import { MetrikaService } from './core/services/metrika.service';
import { UserStore } from './store/user.store';
import { DataCollectionNoticeComponent } from './shared/components/data-collection-notice/data-collection-notice.component';

declare global {
  interface Window {
    ExternalScripts?: {
      loadAll: () => void;
      syncCrm2ChatWidget: (shouldShow: boolean) => void;
    };
  }
}

@Component({
  selector: 'app-root',
  imports: [RouterOutlet, TuiRoot, TuiLoader, DataCollectionNoticeComponent],
  templateUrl: './app.component.html',
  providers: [{ provide: LOCALE_ID, useValue: 'ru' }],
})
export class AppComponent implements OnInit {
  title = 'Reforce';
  currentYear = new Date().getFullYear();
  private readonly isBrowser = isPlatformBrowser(inject(PLATFORM_ID));
  private readonly route = inject(ActivatedRoute);
  private readonly seo = inject(SeoService);
  private readonly metrika = inject(MetrikaService);

  constructor(
    private router: Router,
    private oidcLoginService: OidcLoginService,
    private promoCodeStoreService: PromoCodeStoreService,
    private apiProductsService: ApiProductsService,
    private apiObjectsService: ApiObjectsService,
    private categoriesStore: CategoriesStore,
    private objectTypesStore: ObjectTypesStore,
    private areasStore: AreasStore,
    private userStore: UserStore,
    private userIdentityService: UserIdentityService,
    private apiWebsocetService: ApiWebsocetService,
    private centrifugeService: CentrifugeService,
    protected crm2ChatWidget: Crm2ChatWidgetService,
  ) {}

  public ngOnInit(): void {
    registerLocaleData(localeRu);
    this.initSeo();

    // Всё ниже завязано на браузер: API-вызовы по относительным URL на сервере
    // уходят обратно в SSR-обработчик (рекурсия), а остальное — window/document/
    // localStorage/OIDC/websockets/devtools. Данные грузим на клиенте.
    if (!this.isBrowser) {
      return;
    }

    this.getInitFirstData();
    this.promoCodeStoreService.initPromo();

    // Загружаем скрипты
    if (environment.production && window.ExternalScripts) {
      window.ExternalScripts.loadAll();
    }

    // Отслеживание роутера для Яндекс.Метрики
    this.router.events
      .pipe(filter((event) => event instanceof NavigationEnd))
      .subscribe((event: NavigationEnd) => {
        window.scrollTo(0, 0);

        this.syncCrm2ChatWidget(event.urlAfterRedirects || event.url);

        this.metrika.hit(event.urlAfterRedirects || event.url, {
          referer: document.referrer || undefined,
          title: document.title,
        });
      });

    // OIDC / centrifuge — общий authChecked$ (тот же, что использует гард заказов)
    this.oidcLoginService.authChecked$.subscribe((isAuthenticated) => {
      if (isAuthenticated) {
        this.userIdentityService
          .getInfo()
          .pipe(
            tap((userInfo) => this.userStore.setUser(userInfo)),
            concatMap(() =>
              environment.realtimeEnabled
                ? this.apiWebsocetService.getToken().pipe(catchError(() => of(null)))
                : of(null),
            ),
          )
          .subscribe((centrifugeToken) => {
            if (centrifugeToken) this.centrifugeService.init(centrifugeToken);
          });
      }
    });

    // Свёрнутая надолго вкладка тротлит таймер плановой перепроверки токена
    // в angular-auth-oidc-client — при возврате фокуса продлеваем сессию
    // принудительно, чтобы не упереться в уже протухший access-токен.
    document.addEventListener('visibilitychange', () => {
      if (document.visibilityState !== 'visible') return;

      this.oidcLoginService.isAuthenticated$.pipe(take(1)).subscribe((isAuthenticated) => {
        if (isAuthenticated) {
          this.oidcLoginService.forceRefreshSession().subscribe();
        }
      });
    });

    if (!environment.production) {
      window.setTimeout(() => {
        import('@ngneat/elf-devtools').then(({ devTools }) => devTools());
      });
    }
  }

  // SEO-метаданные берём из route.data.seo самого вложенного активного роута.
  private initSeo(): void {
    this.router.events.pipe(filter((event) => event instanceof NavigationEnd)).subscribe(() => {
      let active = this.route;
      while (active.firstChild) {
        active = active.firstChild;
      }
      this.seo.update(active.snapshot.data['seo'] ?? {});
    });
  }

  private syncCrm2ChatWidget(url: string): void {
    const path = url.split('?')[0].split('#')[0];
    const routeAllowed =
      path === '/' ||
      path.startsWith('/dom') ||
      path.startsWith('/biznes') ||
      path.startsWith('/partner') ||
      path.startsWith('/katalog') ||
      path.startsWith('/configurator') ||
      path.startsWith('/career');

    this.crm2ChatWidget.setRouteAllowed(routeAllowed);
  }

  private getInitFirstData(): void {
    // These dictionaries are independent. Loading them concurrently removes two
    // avoidable network round-trips from the initial application startup.
    forkJoin({
      objectAreas: this.apiObjectsService.getObjectAreas({}).pipe(catchError(() => of(null))),
      objectTypes: this.apiObjectsService.getObjectTypes().pipe(catchError(() => of(null))),
      categories: this.apiProductsService.getCategories().pipe(catchError(() => of(null))),
    }).subscribe(({ objectAreas, objectTypes, categories }) => {
      if (objectAreas) this.areasStore.updateAreas(objectAreas);
      if (objectTypes) this.objectTypesStore.updateObjectTypes(objectTypes);
      if (categories) this.categoriesStore.updateCategories(categories);
    });
  }
}
